// User types
export interface User {
  id: number;
  email: string;
  name: string;
  role: 'ADMIN' | 'EDITOR' | 'VIEWER';
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Event types
export interface Event {
  id: number;
  slug: string;
  title: string;
  description?: string;
  shortDescription?: string;
  eventDate: string;
  eventTime?: string;
  endDate?: string;
  location?: string;
  address?: string;
  templeId?: number;
  category: EventCategory;
  status: EventStatus;
  publishedAt?: string;
  scheduledAt?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export enum EventCategory {
  LITURGY = 'LITURGY',
  MEETING = 'MEETING',
  FEAST = 'FEAST',
  EDUCATION = 'EDUCATION',
  CHARITY = 'CHARITY',
  OTHER = 'OTHER',
}

export enum EventStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

// Archive types
export interface ArchiveItem {
  id: number;
  title: string;
  type: 'DOCUMENT' | 'PHOTO' | 'VIDEO' | 'NEWSPAPER' | 'OTHER';
  category: string;
  year: number;
  description?: string;
  fileUrl: string;
  thumbnailUrl?: string;
  fileSize?: number;
  mimeType?: string;
  tags?: string[];
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// Deanery types
export interface Deanery {
  id: number;
  name: string;
  description?: string;
  coordinatorId?: number;
  coordinator?: Clergy;
  sortOrder: number;
  isActive: boolean;
  temples?: Temple[];
  createdAt: string;
  updatedAt: string;
}

// Temple types
export interface Temple {
  id: number;
  slug: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  schedule?: string;
  history?: string;
  description?: string;
  directions?: string;
  photoUrl?: string;
  latitude?: number;
  longitude?: number;
  deaneryId?: number;
  rectorId?: number;
  rector?: Clergy;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Clergy types
export interface Clergy {
  id: number;
  slug: string;
  name: string;
  rank?: string;
  position?: string;
  biography?: string;
  education?: string;
  awards?: string;
  photoUrl?: string;
  templeId?: number;
  email?: string;
  phone?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// News types
export interface News {
  id: number;
  slug: string;
  title: string;
  excerpt?: string;
  coverImageUrl?: string;
  dateIso?: string;
  dateDisplay?: string;
  contentHtml?: string;
  contentText?: string;
  templeId?: number;
  authorId?: number;
  author?: User;
  originalUrl?: string;
  status: NewsStatus;
  publishedAt?: string;
  scheduledAt?: string;
  images?: NewsImage[];
  videos?: NewsVideo[];
  createdAt: string;
  updatedAt: string;
}

export enum NewsStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  HIDDEN = 'HIDDEN',
  ARCHIVED = 'ARCHIVED',
}

export interface NewsImage {
  id: number;
  newsId: number;
  imageUrl: string;
  displayOrder: number;
  createdAt: string;
}

export interface NewsVideo {
  id: number;
  newsId: number;
  videoUrl: string;
  displayOrder: number;
  createdAt: string;
}

// Gallery types
export interface Gallery {
  id: number;
  title: string;
  description?: string;
  coverImageUrl?: string;
  isPublic: boolean;
  images?: GalleryImage[];
  createdAt: string;
  updatedAt: string;
}

export interface GalleryImage {
  id: number;
  galleryId: number;
  imageUrl: string;
  title?: string;
  description?: string;
  displayOrder: number;
  createdAt: string;
}

// Video types
export interface Video {
  id: number;
  title: string;
  videoUrl: string;
  duration?: string;
  thumbnailUrl?: string;
  category?: string;
  description?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// Necropolis types
export interface Necropolis {
  id: number;
  slug: string;
  name: string;
  rank?: string;
  years?: string;
  portraitUrl?: string;
  biography?: string;
  createdAt: string;
  updatedAt: string;
}

// Newspaper types
export interface Newspaper {
  id: number;
  title: string;
  issueNumber?: string;
  year: number;
  pdfUrl?: string;
  coverUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// Document types
export interface Document {
  id: number;
  title: string;
  category?: string;
  fileUrl?: string;
  fileSize?: string;
  mimeType?: string;
  createdAt: string;
  updatedAt: string;
}

// Setting types
export interface Setting {
  id: number;
  key: string;
  value: string;
  category: 'GENERAL' | 'SEO' | 'SOCIAL' | 'CONTACTS';
  description?: string;
  updatedAt: string;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
 }

// Saint Archive Data (stored in ArchiveItem.description as JSON)
export interface SaintArchiveData {
  feastDay: string;      // День памяти
  prayers: string;       // Молитвы (HTML)
  history: string;       // История почитания (HTML)
  iconUrl: string;       // Икона
}

// Schedule (расписание богослужений)
export interface Schedule {
  id: number;
  name: string;
  location: string;
  url?: string;
  temple_id?: number | null;
  temples?: { id: number; name: string; slug: string } | null;
  created_at?: string;
  updated_at?: string;
}

// Feast (престольный праздник)
export interface Feast {
  id: number;
  name: string;
  date?: string;
  description?: string;
  templeId?: number | null;
  temple?: { id: number; name: string; slug: string } | null;
}

// Saint (красногорские святые) — content is a JSON array for the public viewer
export interface Saint {
  id: number;
  slug: string;
  title: string;
  content: unknown;
  created_at?: string;
  updated_at?: string;
}
